import { useEffect } from "react";
import { Layout } from "@/components/layout";
import { Accessibility as AccessibilityIcon } from "lucide-react";
import { SEOHead } from "@/components/seo";

const Accessibility = () => {
  useEffect(() => {
    document.title = "Accessibility Statement | Prime Realty Partners - New Jersey Real Estate";
  }, []);

  return (
    <Layout>
      <SEOHead
        title="Accessibility Statement"
        description="Prime Realty Partners is committed to digital accessibility. Learn how we ensure our website is usable for all visitors, including those with disabilities."
        canonicalUrl="https://www.prpsj.com/accessibility"
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "Accessibility", url: "https://www.prpsj.com/accessibility" }]}
      />

      <div className="container py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="p-4 bg-primary/10 rounded-lg">
              <AccessibilityIcon className="h-12 w-12 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold">
                Accessibility Statement
              </h1>
              <p className="text-xl text-muted-foreground mt-2">
                Our commitment to digital accessibility
              </p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                Our Commitment
              </h2>
              <p className="text-muted-foreground">
                Prime Realty Partners is committed to ensuring digital accessibility for people with 
                disabilities. We are continually improving the user experience for everyone and 
                applying the relevant accessibility standards to ensure we provide equal access to 
                all users.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                Conformance Status
              </h2>
              <p className="text-muted-foreground">
                We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA. 
                These guidelines explain how to make web content more accessible for people with 
                disabilities and more user-friendly for everyone.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                Accessibility Features
              </h2>
              <p className="text-muted-foreground mb-4">
                Our website includes the following accessibility features:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Keyboard Navigation</strong> - All functionality is accessible via keyboard</li>
                <li><strong>Skip Links</strong> - Skip to main content links for screen reader users</li>
                <li><strong>Alt Text</strong> - Descriptive alternative text for images</li>
                <li><strong>Color Contrast</strong> - Sufficient color contrast ratios for text readability</li>
                <li><strong>Resizable Text</strong> - Text can be resized without loss of functionality</li>
                <li><strong>Form Labels</strong> - All form fields have associated labels</li>
                <li><strong>Focus Indicators</strong> - Visible focus states for interactive elements</li>
                <li><strong>Semantic HTML</strong> - Proper heading structure and landmarks</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                Assistive Technologies
              </h2>
              <p className="text-muted-foreground">
                Our website is designed to be compatible with the following assistive technologies:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>Screen readers (JAWS, NVDA, VoiceOver)</li>
                <li>Screen magnification software</li>
                <li>Speech recognition software</li>
                <li>Keyboard-only navigation</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                Known Limitations
              </h2>
              <p className="text-muted-foreground mb-4">
                While we strive for full accessibility, some content may have limitations:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Some older PDF documents may not be fully accessible</li>
                <li>Third-party content or applications may not meet all accessibility standards</li>
                <li>Some interactive maps may have limited accessibility</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                We are actively working to address these limitations and improve accessibility across 
                all areas of our website.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                Alternative Access
              </h2>
              <p className="text-muted-foreground">
                If you need assistance accessing any content or features on our website, please 
                contact us. We are happy to provide information in alternative formats or assist 
                you in other ways.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                Feedback
              </h2>
              <p className="text-muted-foreground">
                We welcome your feedback on the accessibility of our website. If you encounter 
                accessibility barriers or have suggestions for improvement, please contact us:
              </p>
              <div className="bg-muted/50 rounded-lg p-6 mt-4">
                <p className="font-semibold">Accessibility Coordinator</p>
                <p className="text-muted-foreground">Prime Realty Partners</p>
                <p className="text-muted-foreground">Email: info@prpsj.com</p>
                <p className="text-muted-foreground">Phone: (856) 210-3000</p>
                <p className="text-muted-foreground mt-4">
                  We aim to respond to accessibility feedback within 2 business days.
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                Enforcement
              </h2>
              <p className="text-muted-foreground">
                If you are not satisfied with our response to your accessibility concern, you may 
                contact your local disability rights organization or file a complaint with the 
                U.S. Department of Justice.
              </p>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Accessibility;
